import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Play, X, Sparkles, FileText, Film, Camera, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MarketingShell from "@/components/marketing/MarketingShell";

const HOW_IT_WORKS = [
    { n: "01", title: "Share your project brief", body: "Drop the campaign idea, brand context, and any reference assets. The CEO Agent reads, structures, and asks only what's missing." },
    { n: "02", title: "CEO Agent structures your workflow", body: "Furmais maps the right specialist agents to your stages and locks the brand voice, duration, and visual direction." },
    { n: "03", title: "Specialist agents generate stage outputs", body: "Brand DNA, Research, Concepts, Script, Storyboard — each stage produced with verified facts and flagged assumptions." },
    { n: "04", title: "Review, refine, approve, export", body: "Approve per-stage, regenerate selectively, and export client-ready PDFs, presentations, and share links." },
];

const SERVICES = [
    { id: "campaign", icon: FileText, title: "Campaign Production", body: "Complete brand strategy, research, concepts, scripts, storyboards, and presentation-ready outputs.", status: "Live" },
    { id: "catalog",  icon: Camera,    title: "Catalog & Product Visuals", body: "AI-assisted product concepts, styling directions, and catalog visual planning for fashion, jewelry, and product brands.", status: "Expansion" },
    { id: "film",     icon: Film,     title: "Film & Story Development", body: "Story ideation, screenplay structuring, cinematic breakdowns, and storyboard generation.", status: "Expansion" },
];

const SAMPLE_OUTPUTS = [
    { label: "Brand DNA",       hint: "SWOT · key insights · positioning" },
    { label: "Research Report", hint: "Audience · market · top insights" },
    { label: "Script",          hint: "3 variants · hook · VO · CTA" },
    { label: "Storyboard",      hint: "Shot list · timing · visual cues" },
    { label: "Presentation Deck", hint: "Client-ready PDF + PPTX" },
    { label: "Client Review Link", hint: "Public, brand-styled, approve in-line" },
];

const TRUST_PILLARS = [
    "AI-led production pipeline",
    "Strategy, script & storyboard outputs",
    "Client-ready PDFs & presentations",
    "Built for real agency workflows",
];

const STAGE_PIPS = [
    "Brief", "Competitor", "Brand DNA", "Research", "Plan", "Concepts",
    "Script", "Storyboard", "Prompts", "Deck", "QC", "Delivery",
];

const DEMOS = [
    { id: "yt-hero",  label: "Furmais Hero Reel · 60s", embed: "https://www.youtube.com/embed/BqGR0pXARJA" },
    { id: "yt-flow",  label: "Pipeline Walkthrough",    embed: "https://www.youtube.com/embed/BqGR0pXARJA" },
    { id: "yt-trust", label: "Trust & Integrity",       embed: "https://www.youtube.com/embed/BqGR0pXARJA" },
];

export default function Home() {
    const { user } = useAuth();
    const [demo, setDemo] = useState(null);

    // Authed visitors go straight to the cockpit — preserved behaviour.
    if (user) return <Navigate to="/dashboard" replace />;

    return (
        <MarketingShell
            title="Furmais — AI Production OS for Campaigns, Storyboards & Creative Workflows"
            description="Furmais helps agencies, studios, creators, and brands turn ideas into strategy, scripts, storyboards, presentations, and production-ready outputs using AI-powered workflows."
            ogTitle="Furmais — Your Wish, Powered by AI"
            ogDescription="AI Production OS for agencies, creators, studios, and brands."
        >
            <FlowingLines />

            {/* HERO — desktop: copy left, Genie MP4 right · mobile: copy first, PNG fallback below */}
            <section className="relative z-10 px-6 sm:px-10 pt-12 sm:pt-20 pb-16 max-w-6xl mx-auto" data-testid="home-hero">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-10 items-center">
                    <div>
                        <div className="label-micro mb-4 text-[#FF4400]">Living AI Operating System</div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight max-w-3xl leading-[1.05]" data-testid="home-headline">
                            What&apos;s your <span className="text-[#FF4400]">Furmais</span> today?
                        </h1>
                        <p className="text-zinc-400 text-base sm:text-lg mt-5 max-w-xl" data-testid="home-subhead">
                            Your wish, powered by AI.
                        </p>
                        <p className="text-zinc-500 text-sm sm:text-base mt-3 max-w-xl">
                            Turn a simple brief into strategy, scripts, storyboards, presentations,
                            and production-ready creative outputs through an AI-led workflow.
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-8">
                            <Link to="/login" data-testid="home-cta-start" className="btn-accent text-sm sm:text-base">
                                Start a Project <ArrowRight className="w-4 h-4" />
                            </Link>
                            <button
                                type="button"
                                data-testid="home-cta-demo"
                                onClick={() => setDemo(DEMOS[0])}
                                className="btn-secondary text-sm sm:text-base"
                            >
                                <Play className="w-4 h-4" /> View Demo Output
                            </button>
                        </div>
                        <p className="text-xs font-mono uppercase tracking-wider text-zinc-600 mt-6">
                            Built for agencies, studios, creators, and modern brands.
                        </p>
                    </div>

                    {/* Genie hero — MP4 on desktop, PNG fallback on mobile or video failure */}
                    <HeroGenie />
                </div>
            </section>

            {/* TRUST BANNER */}
            <section className="relative z-10 px-6 sm:px-10 pb-16 max-w-6xl mx-auto" data-testid="home-trust">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {TRUST_PILLARS.map((t, i) => (
                        <div key={i} className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4 flex items-start gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4400] mt-2 shrink-0" />
                            <span className="text-sm text-zinc-200 leading-snug">{t}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-6xl mx-auto" data-testid="home-how-it-works">
                <div className="label-micro mb-3">How it works</div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">
                    Brief in. <span className="text-zinc-500">Production-ready output.</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {HOW_IT_WORKS.map(s => (
                        <div key={s.n} className="border border-zinc-900 bg-[#080808] rounded-sm p-5 hover:border-[#FF4400]/40 transition-colors">
                            <div className="flex items-baseline gap-3 mb-2">
                                <span className="text-[#FF4400] font-mono text-xs">{s.n}</span>
                                <span className="text-base text-white font-medium">{s.title}</span>
                            </div>
                            <p className="text-sm text-zinc-400 leading-relaxed pl-9">{s.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICES / USE CASES */}
            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-6xl mx-auto" data-testid="home-services">
                <div className="label-micro mb-3">Services</div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">
                    Three modes. <span className="text-zinc-500">One operating system.</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {SERVICES.map(s => (
                        <div key={s.id} className="border border-zinc-900 bg-[#080808] rounded-sm p-5 flex flex-col gap-3" data-testid={`home-service-${s.id}`}>
                            <div className="flex items-center justify-between">
                                <s.icon className="w-5 h-5 text-[#FF4400]" />
                                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${s.status === "Live" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                                    {s.status === "Live" ? "Live" : "Expansion · Coming Soon"}
                                </span>
                            </div>
                            <div className="text-base text-white font-medium">{s.title}</div>
                            <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SAMPLE OUTPUTS */}
            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-6xl mx-auto" data-testid="home-samples">
                <div className="label-micro mb-3">Sample outputs</div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">
                    Every stage, <span className="text-zinc-500">client-ready.</span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SAMPLE_OUTPUTS.map(s => (
                        <div key={s.label} className="border border-zinc-900 bg-[#080808] rounded-sm p-4 hover:border-[#FF4400]/40 transition-colors">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-sm text-white font-medium">{s.label}</span>
                                <Sparkles className="w-3.5 h-3.5 text-[#FF4400]" />
                            </div>
                            <p className="text-[11px] font-mono text-zinc-500">{s.hint}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* THE PIPELINE */}
            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-6xl mx-auto" data-testid="home-pipeline-section">
                <div className="label-micro mb-4">The Pipeline</div>
                <div className="flex flex-wrap gap-2" data-testid="home-pipeline" style={{ "--n": STAGE_PIPS.length }}>
                    {STAGE_PIPS.map((label, i) => (
                        <div key={label} className="stage-pip px-3 py-2 border rounded-sm text-xs font-mono uppercase tracking-wider"
                            style={{ "--i": i, "--n": STAGE_PIPS.length }}>
                            {String(i).padStart(2, "0")} · {label}
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT (preview block) */}
            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-6xl mx-auto" data-testid="home-about">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8 items-start">
                    <div>
                        <div className="label-micro mb-3">About Furmais</div>
                        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">An AI production system, not just an AI tool.</h2>
                    </div>
                    <p className="text-zinc-400 text-base leading-relaxed">
                        Furmais is an AI-powered production operating system designed to transform
                        ideas into structured creative execution. It combines strategy, research,
                        writing, storyboarding, and production planning into one intelligent
                        workflow. Unlike traditional tools, Furmais doesn't just assist —
                        it <span className="text-white">orchestrates</span>.
                        <br /><br />
                        From a simple brief, it builds a complete production-ready pipeline used
                        by agencies, creators, studios, and brands.
                        <Link to="/about" data-testid="home-read-more" className="ml-2 text-[#FF4400] hover:underline">
                            Read the full story →
                        </Link>
                    </p>
                </div>
            </section>

            {/* CONTACT TEASER */}
            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-6xl mx-auto" data-testid="home-contact-teaser">
                <div className="border border-zinc-900 bg-[#080808] rounded-sm p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
                    <div>
                        <div className="label-micro mb-2">Talk to us</div>
                        <h3 className="font-display text-2xl sm:text-3xl tracking-tight">Need a demo or a custom production workflow?</h3>
                        <p className="text-zinc-400 text-sm mt-2">Reach the team behind Furmais — fastest replies on WhatsApp.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a
                            href="https://wa.me/918866065375"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="home-whatsapp"
                            className="btn-accent text-sm"
                        >
                            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                        </a>
                        <Link to="/contact" data-testid="home-contact-link" className="btn-secondary text-sm">
                            Contact &amp; Support
                        </Link>
                    </div>
                </div>
            </section>

            {/* DEMO VIDEO STRIP */}
            <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <div className="label-micro">Watch in 60 seconds</div>
                    <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">3 short clips</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {DEMOS.map(d => (
                        <button key={d.id} type="button" data-testid={`home-demo-${d.id}`}
                            onClick={() => setDemo(d)}
                            className="group relative overflow-hidden border border-zinc-900 hover:border-[#FF4400]/50 rounded-sm p-4 text-left transition-colors">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Demo</div>
                                    <div className="text-sm mt-1">{d.label}</div>
                                </div>
                                <div className="w-9 h-9 rounded-full border border-zinc-700 group-hover:border-[#FF4400] flex items-center justify-center">
                                    <Play className="w-3.5 h-3.5 text-zinc-300 group-hover:text-[#FF4400]" />
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {demo && <DemoModal demo={demo} onClose={() => setDemo(null)} />}
        </MarketingShell>
    );
}

/** Hero mascot — MP4 on desktop (≥md), PNG on mobile (<md).
 *  Uses Tailwind's `md:hidden` / `hidden md:block` for clean responsive
 *  splits. Browsers that can't play MP4 fall back to the inner `<img>`. */
function HeroGenie() {
    return (
        <div className="relative mx-auto w-full" data-testid="hero-genie">
            <div className="absolute inset-0 rounded-full bg-[#FF4400]/15 blur-3xl genie-pulse" aria-hidden="true" />

            {/* MOBILE — PNG only (no autoplay video on mobile per spec) */}
            <div className="md:hidden mx-auto max-w-md aspect-square flex items-center justify-center" data-testid="hero-genie-fallback">
                <img
                    src="/genie/genie.png"
                    alt="Furmais AI Genie mascot"
                    loading="lazy"
                    decoding="async"
                    width="640"
                    height="640"
                    className="relative w-full h-full object-contain genie-float"
                />
            </div>

            {/* DESKTOP — MP4. The inner <img> is the native fallback if a
                browser can't decode video/mp4. */}
            <video
                className="hidden md:block relative w-full aspect-video object-contain rounded-sm"
                data-testid="hero-genie-video"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/genie/genie.png"
                aria-label="Furmais AI Genie mascot"
            >
                <source src="/genie/genie.mp4" type="video/mp4" />
                <img src="/genie/genie.png" alt="Furmais AI Genie mascot" className="w-full h-full object-contain" />
            </video>
        </div>
    );
}


function FlowingLines() {
    return (
        <div className="flowing-lines" aria-hidden="true">
            <svg preserveAspectRatio="none" viewBox="0 0 1440 900">
                <defs>
                    <linearGradient id="flowLineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%"   stopColor="#FF4400" stopOpacity="0" />
                        <stop offset="40%"  stopColor="#FF4400" stopOpacity="0.7" />
                        <stop offset="60%"  stopColor="#FF6E2C" stopOpacity="0.7" />
                        <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d="M-200,200 C 280,80 660,360 1080,180 S 1640,420 1840,240" />
                <path d="M-200,460 C 280,340 660,560 1080,420 S 1640,640 1840,500" />
                <path d="M-200,720 C 240,580 700,820 1080,660 S 1700,780 1840,720" />
                <path d="M-200,860 C 320,720 700,920 1100,800 S 1700,940 1840,880" />
            </svg>
        </div>
    );
}

function DemoModal({ demo, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="home-demo-modal" onClick={onClose}>
            <div className="w-full max-w-3xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm">{demo.label}</div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="home-demo-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="aspect-video w-full border border-zinc-800 rounded-sm overflow-hidden">
                    <iframe title={demo.label} loading="lazy" src={demo.embed}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen className="w-full h-full" />
                </div>
            </div>
        </div>
    );
}
